import { useEffect, useState } from "react";

/** The two `URL` statics an object-URL lease needs (injectable so the rule is testable). */
export type ObjectUrlApi = Pick<typeof URL, "createObjectURL" | "revokeObjectURL">;

/**
 * #1969 — lease an object URL for a Blob. `release` revokes it, once: a second call is a no-op,
 * so an effect cleanup that runs twice (React strict mode) cannot revoke a URL twice or throw.
 *
 * An object URL pins its Blob in memory until it is revoked or the document unloads. The console
 * is a single-page app that is rarely reloaded, so a rendered PDF (up to 10 MiB) whose URL is
 * never revoked would stay resident for the rest of the session.
 */
export function leaseObjectUrl(
  blob: Blob,
  api: ObjectUrlApi = URL,
): { url: string; release: () => void } {
  const url = api.createObjectURL(blob);
  let released = false;
  return {
    url,
    release: () => {
      if (released) return;
      released = true;
      api.revokeObjectURL(url);
    },
  };
}

/**
 * An object URL for `blob` while the component is mounted: created when the Blob arrives,
 * revoked when it changes and when the component unmounts. `null` until there is a Blob.
 */
export function useObjectUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return undefined;
    }
    const lease = leaseObjectUrl(blob);
    setUrl(lease.url);
    return lease.release;
  }, [blob]);

  return url;
}
